import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FiBook, FiFileText, FiDownload, FiX, FiChevronRight, FiLoader } from "react-icons/fi";
import { getDocuments, UnitDocument } from "../../../actions/document-actions";
import SnackbarComponent from "../../../components/Snackbar/Snackbar";
import { useSnack } from "../../../hooks/useSnack";
import "./Guides.scss";

function Guides() {
    const { t } = useTranslation();
    const [documents, setDocuments] = useState<UnitDocument[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedDocument, setSelectedDocument] = useState<UnitDocument | null>(null);
    const snack = useSnack();

    useEffect(() => {
        loadDocuments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadDocuments = async () => {
        setIsLoading(true);
        try {
            const response = await getDocuments('Guide');
            setDocuments(response.data);
        } catch (error: any) {
            snack.setMessage(t("guidesPage.loadError"));
            snack.setSeverity("error");
            snack.onOpen();
        } finally {
            setIsLoading(false);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const openDocument = (doc: UnitDocument) => {
        setSelectedDocument(doc);
    };

    const closeViewer = () => {
        setSelectedDocument(null);
    };

    const downloadDocument = (doc: UnitDocument) => {
        window.open(doc.blobUrl, '_blank');
    };

    return (
        <>
            <div className="guides-page dark-theme">
                <div className="guides-container">
                    {/* Header */}
                    <div className="guides-header">
                        <div className="guides-header__icon">
                            <FiBook size={36} />
                        </div>
                        <h1 className="guides-header__title">{t("guidesPage.title")}</h1>
                        <p className="guides-header__subtitle">
                            {t("guidesPage.subtitle")}
                        </p>
                    </div>

                    {/* Content */}
                    {isLoading ? (
                        <div className="guides-loading">
                            <FiLoader className="guides-loading__spinner" size={32} />
                            <p>{t("guidesPage.loading")}</p>
                        </div>
                    ) : documents.length === 0 ? (
                        <div className="guides-empty">
                            <div className="guides-empty__icon">
                                <FiFileText size={28} />
                            </div>
                            <h2 className="guides-empty__title">{t("guidesPage.noGuides")}</h2>
                            <p className="guides-empty__text">
                                {t("guidesPage.noGuidesText")}
                            </p>
                        </div>
                    ) : (
                        <div className="guides-list">
                            {documents.map((doc) => (
                                <div
                                    key={doc.id}
                                    className="guides-card"
                                    onClick={() => openDocument(doc)}
                                >
                                    <div className="guides-card__icon">
                                        <FiFileText size={24} />
                                    </div>
                                    <div className="guides-card__content">
                                        <h3 className="guides-card__title">{doc.name}</h3>
                                        {doc.description && (
                                            <p className="guides-card__description">{doc.description}</p>
                                        )}
                                        <span className="guides-card__size">{formatFileSize(doc.fileSizeBytes)}</span>
                                    </div>
                                    <div className="guides-card__chevron">
                                        <FiChevronRight size={20} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* PDF Viewer Modal */}
            {selectedDocument && (
                <PdfViewerModal
                    document={selectedDocument}
                    onClose={closeViewer}
                    onDownload={() => downloadDocument(selectedDocument)}
                />
            )}

            <SnackbarComponent
                isOpen={snack.isOpen}
                severity={snack.severity}
                onClose={snack.onClose}
                message={snack.message}
            />
        </>
    );
}

// PDF Viewer Modal Component
interface PdfViewerModalProps {
    document: UnitDocument;
    onClose: () => void;
    onDownload: () => void;
}

const PdfViewerModal: React.FC<PdfViewerModalProps> = ({ document, onClose, onDownload }) => {
    const [isLoading, setIsLoading] = useState(true);

    const handleIframeLoad = () => {
        setIsLoading(false);
    };

    // Use Google Docs Viewer to display PDF inline (prevents auto-download)
    const getViewerUrl = (url: string): string => {
        const encodedUrl = encodeURIComponent(url);
        return `https://docs.google.com/gview?url=${encodedUrl}&embedded=true`;
    };

    return (
        <div className="pdf-viewer-modal">
            {/* Header */}
            <div className="pdf-viewer-modal__header">
                <button className="pdf-viewer-modal__back" onClick={onClose}>
                    <FiX size={24} />
                </button>
                <h3 className="pdf-viewer-modal__title">{document.name}</h3>
                <button className="pdf-viewer-modal__download" onClick={onDownload}>
                    <FiDownload size={20} />
                </button>
            </div>

            {/* PDF Content */}
            <div className="pdf-viewer-modal__content">
                {isLoading && (
                    <div className="pdf-viewer-modal__loading">
                        <FiLoader className="pdf-viewer-modal__spinner" size={40} />
                    </div>
                )}
                <iframe
                    src={getViewerUrl(document.blobUrl)}
                    className="pdf-viewer-modal__iframe"
                    title={document.name}
                    onLoad={handleIframeLoad}
                    style={{ opacity: isLoading ? 0 : 1 }}
                />
            </div>
        </div>
    );
};

export default Guides;
